import { useTheme } from 'styled-components';

/**
 * Popup shown when a new deployment is detected.
 * User clicks "UPDATE NOW!" to reload — no forced auto-reload,
 * which naturally spreads reload load across users.
 */
interface AppVersionUpdateProps {
  onUpdate: () => void;
}

export const AppVersionUpdate = ({ onUpdate }: AppVersionUpdateProps) => {
  const theme = useTheme() as { palette?: { primary?: { main?: string } } };
  const primary = theme?.palette?.primary?.main ?? '#7C2FB5';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(15, 23, 42, 0.45)',
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        animation: 'vuFadeIn 0.25s ease-out',
        padding: 16,
      }}
    >
      <style>{`
        @keyframes vuFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes vuRocketFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes vuFlame {
          0%, 100% { transform: scaleY(1); opacity: 1; }
          50% { transform: scaleY(1.15); opacity: 0.85; }
        }
      `}</style>

      <div
        style={{
          width: 360,
          maxWidth: '100%',
          background: '#fff',
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top section — uses the app's primary color */}
        <div
          style={{
            position: 'relative',
            background: primary,
            padding: '40px 24px 0',
            textAlign: 'center',
            overflow: 'hidden',
          }}
        >
          {/* Star dots */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              opacity: 0.35,
              backgroundImage: `
                radial-gradient(circle, #fff 1px, transparent 1.5px),
                radial-gradient(circle, #fff 1px, transparent 1.5px)
              `,
              backgroundSize: '60px 80px, 90px 120px',
              backgroundPosition: '10px 20px, 40px 60px',
            }}
          />

          <h2
            style={{
              position: 'relative',
              margin: 0,
              fontSize: 24,
              fontWeight: 700,
              color: '#fff',
              letterSpacing: -0.3,
              lineHeight: 1.3,
            }}
          >
            New Update is
            <br />
            Available
          </h2>

          {/* Rocket + flame + cloud */}
          <div
            style={{
              position: 'relative',
              marginTop: 10,
              height: 220,
            }}
          >
            {/* Rocket + flame — one SVG so they stay aligned.
                zIndex: 3 so the flame sits in front of the cloud. */}
            <svg
              viewBox="0 0 120 180"
              width="120"
              height="180"
              style={{
                display: 'block',
                margin: '0 auto',
                position: 'relative',
                zIndex: 3,
                animation: 'vuRocketFloat 2.4s ease-in-out infinite',
              }}
            >
              {/* Body */}
              <path
                d="M60 8 C75 22 82 48 82 78 L82 118 L38 118 L38 78 C38 48 45 22 60 8 Z"
                fill="#F1F5F9"
                stroke="#CBD5E1"
                strokeWidth="1"
              />
              {/* Body center stripe */}
              <path
                d="M60 8 C62 22 62 50 62 78 L62 118 L58 118 L58 78 C58 50 58 22 60 8 Z"
                fill="#E2E8F0"
              />
              {/* Window */}
              <circle cx="60" cy="58" r="12" fill="#475569" />
              <circle cx="60" cy="58" r="9" fill="#334155" />
              <circle cx="60" cy="58" r="7" fill="#22D3EE" />
              <circle cx="57" cy="55" r="2" fill="#A5F3FC" opacity="0.8" />
              {/* Left fin */}
              <path d="M38 90 L20 120 L20 130 L38 118 Z" fill="#F97316" />
              {/* Right fin */}
              <path d="M82 90 L100 120 L100 130 L82 118 Z" fill="#F97316" />
              {/* Bottom nozzle */}
              <rect x="52" y="118" width="16" height="8" fill="#64748B" rx="1" />
              {/* Flame — grouped so it flickers in place under nozzle centre.
                  Wide at top where it meets the nozzle, pointy at the bottom. */}
              <g
                style={{
                  transformOrigin: '60px 126px',
                  animation: 'vuFlame 0.4s ease-in-out infinite',
                }}
              >
                {/* Outer yellow — wide rounded top at the nozzle, rounded bottom */}
                <path
                  d="M 54 126
                     C 50 138 48 150 52 160
                     C 55 170 58 174 60 174
                     C 62 174 65 170 68 160
                     C 72 150 70 138 66 126
                     Z"
                  fill="#FBBF24"
                />
                {/* Inner orange — smaller flame, same rounded top */}
                <path
                  d="M 56 130
                     C 53 140 52 150 55 158
                     C 57 166 59 170 60 170
                     C 61 170 63 166 65 158
                     C 68 150 67 140 64 130
                     Z"
                  fill="#F97316"
                />
              </g>
            </svg>
          </div>

          {/* Cloud — shorter, tight against the text below. Flame sits in front (zIndex 3 > 2). */}
          <svg
            viewBox="0 0 360 90"
            width="100%"
            height="90"
            preserveAspectRatio="none"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: -1,
              zIndex: 2,
            }}
          >
            {/* Purple-tinted back layer — highest bumps */}
            <path
              d="M0 90
                 L0 40
                 C10 22 28 14 46 22
                 C56 2 90 0 108 18
                 C120 -2 158 -2 176 20
                 C188 0 224 2 240 22
                 C252 2 286 4 302 24
                 C316 10 342 14 360 28
                 L360 90 Z"
              fill="#C4B5FD"
              opacity="0.55"
            />
            {/* White front layer — billowy puffs */}
            <circle cx="32" cy="54" r="26" fill="#fff" />
            <circle cx="72" cy="42" r="32" fill="#fff" />
            <circle cx="120" cy="50" r="28" fill="#fff" />
            <circle cx="168" cy="38" r="34" fill="#fff" />
            <circle cx="220" cy="48" r="32" fill="#fff" />
            <circle cx="268" cy="40" r="34" fill="#fff" />
            <circle cx="316" cy="50" r="30" fill="#fff" />
            <circle cx="352" cy="56" r="26" fill="#fff" />
            {/* Solid base so the cloud bottom is flat against the white section */}
            <rect x="0" y="50" width="360" height="40" fill="#fff" />
          </svg>
        </div>

        {/* White bottom section */}
        <div
          style={{
            padding: '8px 28px 24px',
            textAlign: 'center',
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 15,
              lineHeight: 1.55,
              color: '#334155',
            }}
          >
            A new version is released, please update to get new features
          </p>

          <button
            type="button"
            onClick={onUpdate}
            style={{
              marginTop: 22,
              width: '100%',
              padding: '14px 16px',
              border: 'none',
              borderRadius: 10,
              background: primary,
              color: '#fff',
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: 0.5,
              cursor: 'pointer',
              transition: 'transform 0.1s ease, box-shadow 0.1s ease',
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(1px)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            UPDATE NOW!
          </button>
        </div>
      </div>
    </div>
  );
};
